import Button from "@mui/material/Button";
import { useCallback, useContext } from "react";
import { ToastOptions, toast } from "react-toastify";

import { mobileFabBottomOffset } from "~/components/mobile/mobileChrome";
import logger from "~/helpers/logger";
import snackStack, { snackDedupeKey } from "~/helpers/snackStack";
import trialLimitSnackGate from "~/helpers/trialLimitSnackGate";
import { useAppSelector } from "~/hooks/reduxHooks";
import SnackSurfaceContext, { type SnackSurface } from "~/providers/snackSurfaceContext";

export interface OpenSnackOptions {
	skipLog?: boolean;
	surface?: SnackSurface;
	toastId?: string;
}

type SnackVariant = "default" | "error" | "success" | "warning" | "info";

export type OpenSnack = (
	msg: React.ReactNode,
	variant?: SnackVariant,
	persist?: boolean,
	action?: (key: string | number | undefined) => React.ReactNode,
	error?: unknown,
	options?: OpenSnackOptions
) => string | number;

// react-toastify silently drops a toast() call whose id is already active
let nextGlobalToastId = 1;

export const useSnacks = (): [OpenSnack, (key: string | number) => void] => {
	const isWideScreen = useAppSelector((state) => state.layout.isWideScreen);
	const surface = useContext(SnackSurfaceContext);

	const openSnack = useCallback(
		(
			msg: React.ReactNode,
			variant: SnackVariant | undefined = "default",
			persist: boolean = false,
			action?: (key: string | number | undefined) => React.ReactNode,
			error?: unknown,
			snackOptions: OpenSnackOptions = {}
		): string | number => {
			const isMobileSurface = (snackOptions.surface ?? surface) === "mobile";
			const toastId =
				snackOptions.toastId ?? (isMobileSurface ? snackDedupeKey(msg, variant) : null) ?? nextGlobalToastId++;

			if (variant === "error" && trialLimitSnackGate.claimNextErrorSnack()) return toastId;

			if (isMobileSurface && !persist && !action)
				for (const evicted of snackStack.admit(toastId)) toast.dismiss(evicted);

			const options: ToastOptions = {
				autoClose: persist ? false : 5000,
				closeButton: persist ? false : true,
				closeOnClick: !persist,
				draggable: true,
				hideProgressBar: false,
				onClose: () => snackStack.release(toastId),
				pauseOnHover: true,
				position: "bottom-right",
				style: {
					fontSize: isWideScreen ? "1.2em" : ".9em",
					marginBottom: isWideScreen ? undefined : mobileFabBottomOffset,
					minHeight: isWideScreen ? "60px" : "40px",
					minWidth: isWideScreen ? "400px" : "200px"
				}
			};

			// Custom close button for persist mode
			if (persist && !action) {
				options.closeButton = () => (
					<Button
						color="inherit"
						onClick={() => toast.dismiss(toastId)}
						size="small"
						sx={{ minWidth: "auto", padding: "4px 8px" }}
						variant="outlined"
					>
						X
					</Button>
				);
			} else if (action) {
				options.closeButton = () => action(toastId);
			}

			if (!snackOptions.skipLog) {
				if (variant === "error") {
					if (error !== undefined) {
						logger.error(typeof msg === "string" ? msg : "Application error", error);
					} else if (typeof msg === "string") {
						logger.error(msg);
					}
				} else if (variant === "warning" && typeof msg === "string") {
					logger.warn(msg);
				}
			}

			// Map variant to toast type
			const toastMethod = variant === "default" ? toast : toast[variant];

			toastMethod(msg, {
				...options,
				toastId
			});

			return toastId;
		},
		[isWideScreen, surface]
	);

	const closeSnack = useCallback((key: string | number) => {
		snackStack.release(key);
		toast.dismiss(key);
	}, []);

	return [openSnack, closeSnack];
};
